import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Zap, Clock, Users, AlertTriangle, ExternalLink, BookOpen, ArrowRight, Calendar, Video, CheckSquare } from "lucide-react";
import { Link } from "react-router-dom";

const StrideHistory = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": { "@type": "WebPage", "@id": "https://csw.live/stride/history/" },
    "headline": "Stride: Atlassian's Short-Lived HipChat Successor",
    "description": "The history of Stride, Atlassian's ambitious team messaging app that lasted less than two years before being discontinued in 2019.",
    "author": { "@type": "Organization", "name": "CSW Team", "url": "https://csw.live" },
    "publisher": { "@type": "Organization", "name": "CSW - Chill Study Work", "url": "https://csw.live" },
    "datePublished": "2025-01-11",
    "dateModified": "2025-01-11"
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Stride History - Atlassian's Short-Lived Team Chat"
        description="A retrospective on Stride - Atlassian's ambitious HipChat successor that was discontinued in 2019 less than two years after launch."
        keywords="stride history, stride shutdown, atlassian stride, hipchat successor, slack competition"
        canonicalUrl="/stride/history/"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Header />
      
      <main className="pt-20">
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
          <div className="container mx-auto px-4 relative z-10">
            <nav className="mb-8">
              <ol className="flex items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li>/</li>
                <li><Link to="/stride/" className="hover:text-primary">Stride</Link></li>
                <li>/</li>
                <li className="text-foreground">History</li>
              </ol>
            </nav>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Calendar className="w-4 h-4" />
                A Retrospective • 2017-2019
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
                The Stride Story
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                Atlassian's ambitious attempt to reinvent team messaging—and why it lasted less than two years.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="border-primary/20 bg-gradient-to-br from-background to-primary/5">
                <CardContent className="p-8">
                  <p className="text-lg text-muted-foreground">
                    Stride was Atlassian's ambitious but short-lived attempt to reinvent team messaging. Launched in <span className="text-foreground font-semibold">September 2017</span> as the successor to HipChat Cloud, Stride was meant to modernize Atlassian's chat offering with a slew of new features.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-primary/10">
                  <BookOpen className="w-6 h-6 text-primary" />
                </div>
                <h2 className="text-3xl font-bold">Features & Innovation</h2>
              </div>
              
              <div className="space-y-6">
                <p className="text-muted-foreground text-lg">
                  The Stride team built on lessons from HipChat's shortcomings—adding built-in voice/video conferencing, threaded discussions, and lightweight project management tools.
                </p>

                <Card className="border-border/50">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-foreground mb-4">Standout Features</h3>
                    <ul className="grid md:grid-cols-2 gap-3 text-muted-foreground">
                      <li className="flex items-center gap-2"><Video className="w-4 h-4 text-primary" /> Built-in Meetings (voice/video)</li>
                      <li className="flex items-center gap-2"><CheckSquare className="w-4 h-4 text-primary" /> Actions & Decisions tracking</li>
                      <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> Focus Mode (snooze notifications)</li>
                      <li className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> Jira & Confluence integration</li>
                    </ul>
                  </CardContent>
                </Card>

                <p className="text-muted-foreground text-lg">
                  The concept of <span className="text-foreground font-medium">contextual decision tracking</span>—turning any message into a follow-up task or marking decisions—was something pure chat apps lacked. Stride was praised for a slick UI and being available across all platforms at launch.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-destructive/10">
                  <AlertTriangle className="w-6 h-6 text-destructive" />
                </div>
                <h2 className="text-3xl font-bold">Why It Shut Down</h2>
              </div>

              <div className="space-y-6 text-lg text-muted-foreground">
                <p>
                  Unfortunately, Stride never got the chance to fully grow. <span className="text-foreground font-semibold">Barely a year after launch</span>, Atlassian announced in July 2018 that it would discontinue Stride (and HipChat) as part of a strategic partnership with Slack.
                </p>

                <Card className="border-destructive/30 bg-destructive/5">
                  <CardContent className="p-6">
                    <p className="text-foreground font-medium text-xl mb-4">The End</p>
                    <p className="text-muted-foreground">
                      Stride stopped accepting new sign-ups immediately, and existing customers were told the service would shut down on <span className="font-semibold">February 15, 2019</span>. The total lifespan was less than 18 months.
                    </p>
                  </CardContent>
                </Card>

                <p>
                  The root cause was simple—despite improvements, Stride couldn't gain enough traction to justify its existence. Competing against Slack's momentum (and Microsoft's entry with Teams) proved too difficult. Atlassian's CEO acknowledged that continuing to pour resources into a losing battle wasn't wise.
                </p>

                <div className="flex flex-wrap gap-3">
                  <a href="https://techcrunch.com/2018/07/26/atlassians-hipchat-and-stride-to-be-discontinued-with-slack-buying-up-the-ip/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm">
                    <ExternalLink className="w-4 h-4" />
                    TechCrunch: Stride Discontinued
                  </a>
                  <a href="https://www.theverge.com/2018/7/26/17619482/slack-hipchat-acquisition-stride-atlassian-partnership-microsoft-teams-competition" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 text-sm">
                    <ExternalLink className="w-4 h-4" />
                    The Verge: Slack Partnership
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Looking for a <span className="text-gradient">Stride Alternative?</span></h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              CSW offers free virtual collaboration rooms with focus modes and productivity tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try CSW Free <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/stride/">View Stride Alternative</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StrideHistory;
